import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Briefcase, Car, MessageSquare, Users, Globe, ArrowRight, Users2 } from "lucide-react";
import SponsoredAdWidget from "@/components/SponsoredAdWidget";
import CoinsHeroBanner from "@/components/CoinsHeroBanner";
import TestimonialsCarousel from "@/components/TestimonialsCarousel";
import WeeklyRanking from "@/components/WeeklyRanking";
import LiveActivityFeed from "@/components/LiveActivityFeed";
import DownloadAppBar from "@/components/DownloadAppBar";
import LocationFields from "@/components/LocationFields";
import AppLogo, { AppName } from "@/components/AppLogo";
import { trackSignupViewContent } from "@/lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Landing() {
  const { user, setAuth } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", referred_by: "", country: "", state: "", city: "", cep: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total_users: 0, total_drivers: 0, total_rides_completed: 0 });
  const [landing, setLanding] = useState({});

  useEffect(() => {
    axios.get(`${API}/landing/content`).then((r) => setLanding(r.data || {})).catch(() => {});
  }, []);

  // Read ?ref= query param to auto-fill referrer + switch to register tab
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      setForm((f) => ({ ...f, referred_by: ref }));
      setTab("register");
    }
  }, []);

  useEffect(() => {
    if (user) navigate("/feed");
  }, [user, navigate]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/stats/public`);
        if (alive) setStats(r.data);
      } catch (err) { /* ignore */ }
    };
    load();
    const iv = setInterval(load, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  if (user) return null;

  const validateRegister = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Informe seu nome completo";
    else if (form.name.trim().length < 3) e.name = "Nome muito curto";
    if (!form.email.trim()) e.email = "Informe seu e-mail";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "E-mail inválido";
    if (!form.phone.trim()) e.phone = "Informe seu telefone";
    else if (form.phone.replace(/\D/g, "").length < 8) e.phone = "Telefone inválido";
    if (!form.password) e.password = "Informe uma senha";
    else if (form.password.length < 6) e.password = "A senha deve ter pelo menos 6 caracteres";
    if (!(form.country || "").trim()) e.country = "Informe o país";
    if (!(form.state || "").trim()) e.state = "Informe o estado";
    if (!(form.city || "").trim()) e.city = "Informe a cidade";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const setField = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const handleSubmit = async () => {
    if (tab === "register" && !validateRegister()) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    setLoading(true);
    try {
      const url = tab === "login" ? "/auth/login" : "/auth/register";
      const payload = tab === "login" ? { email: form.email, password: form.password } : { email: form.email.trim(), password: form.password, name: form.name.trim(), phone: form.phone.trim(), referred_by: form.referred_by || null, country: form.country.trim(), state: form.state.trim(), city: form.city.trim(), cep: form.cep || null };
      const res = await axios.post(`${API}${url}`, payload);
      setAuth(res.data.token, res.data.user);
      if (tab === "register") trackSignupViewContent(res.data.meta_event_id);
      toast.success(tab === "login" ? "Bem-vindo!" : "Cadastro realizado!");
      navigate("/feed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/feed";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const langLabels = { pt: "PT-BR", en: "EN", es: "ES" };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden pb-24">
      <DownloadAppBar />
      {/* Decorative gradient blobs (blue only, no purple) */}
      <div className="absolute top-0 -left-40 w-96 h-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -right-40 w-96 h-96 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />

      <header className="relative z-10 flex items-center justify-between p-6">
        <div className="flex items-center gap-2">
          <AppLogo size="lg" />
          <AppName className="font-bold text-xl tracking-tight" />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid="lang-switcher" variant="ghost" size="sm" className="gap-2">
              <Globe className="w-4 h-4" />
              {langLabels[lang]}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setLang("pt")} data-testid="lang-pt">🇧🇷 PT-BR</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setLang("en")} data-testid="lang-en">🇺🇸 English</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setLang("es")} data-testid="lang-es">🇪🇸 Español</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <section className="relative z-10 max-w-6xl mx-auto px-6 pb-8" data-testid="landing-auth-section">
        <Card className="max-w-md mx-auto p-6 sm:p-8 shadow-xl border-border/60 bg-card/90 backdrop-blur">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full rounded-full mb-6">
              <TabsTrigger value="login" data-testid="tab-login" className="rounded-full">{t("login")}</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register" className="rounded-full">{t("register")}</TabsTrigger>
            </TabsList>
            <TabsContent value="login" className="space-y-4">
              <Input data-testid="login-email" placeholder={t("email")} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Input data-testid="login-password" placeholder={t("password")} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              <Button data-testid="login-submit" onClick={handleSubmit} disabled={loading} className="w-full rounded-full h-11">
                {loading ? t("loading") : t("login")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <div className="text-center">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-muted-foreground hover:text-primary">
                  Esqueceu a senha?
                </Link>
              </div>
            </TabsContent>
            <TabsContent value="register" className="space-y-4">
              {form.referred_by && (
                <div className="p-2 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-[11px] flex items-center gap-2 text-emerald-800 dark:text-emerald-200" data-testid="register-referral-badge">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  Você foi convidado por <code className="px-1 py-0.5 rounded bg-emerald-500/20 font-semibold">{form.referred_by}</code>
                </div>
              )}
              {[
                { k: "name", testid: "register-name", placeholder: `${t("name")} completo *`, type: "text" },
                { k: "email", testid: "register-email", placeholder: `${t("email")} *`, type: "email" },
                { k: "phone", testid: "register-phone", placeholder: "Telefone *", type: "tel" },
                { k: "password", testid: "register-password", placeholder: `${t("password")} *`, type: "password" },
              ].map((f) => (
                <div key={f.k} className="space-y-1">
                  <Input
                    data-testid={f.testid}
                    placeholder={f.placeholder}
                    type={f.type}
                    value={form[f.k] || ""}
                    onChange={setField(f.k)}
                    aria-invalid={!!errors[f.k]}
                    className={errors[f.k] ? "border-red-500 focus-visible:ring-red-500" : ""}
                  />
                  {errors[f.k] && (
                    <p data-testid={`${f.testid}-error`} className="text-xs text-red-500 pl-1">{errors[f.k]}</p>
                  )}
                </div>
              ))}
              <LocationFields form={form} setForm={setForm} errors={errors} />
              <p className="text-[11px] text-muted-foreground">* Todos os campos são obrigatórios</p>
              <Button data-testid="register-submit" onClick={handleSubmit} disabled={loading} className="w-full rounded-full h-11">
                {loading ? t("loading") : t("register")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </TabsContent>
          </Tabs>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground tracking-widest">{t("or")}</span></div>
          </div>
          <Button data-testid="google-login" variant="outline" onClick={handleGoogle} className="w-full rounded-full h-11 gap-2">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            {t("loginGoogle")}
          </Button>
        </Card>
      </section>

      {landing.announcement_enabled && landing.announcement_text && (() => {
        const COLOR_MAP = {
          amber:   { bg: "rgba(245,158,11,0.14)", border: "rgba(245,158,11,0.45)", fg: "#78350f" },
          emerald: { bg: "rgba(16,185,129,0.14)", border: "rgba(16,185,129,0.45)", fg: "#064e3b" },
          green:   { bg: "rgba(34,197,94,0.14)",  border: "rgba(34,197,94,0.45)",  fg: "#14532d" },
          blue:    { bg: "rgba(37,99,235,0.14)",  border: "rgba(37,99,235,0.45)",  fg: "#1e3a8a" },
          violet:  { bg: "rgba(139,92,246,0.14)", border: "rgba(139,92,246,0.45)", fg: "#4c1d95" },
          red:     { bg: "rgba(220,38,38,0.14)",  border: "rgba(220,38,38,0.45)",  fg: "#7f1d1d" },
          slate:   { bg: "rgba(100,116,139,0.14)",border: "rgba(100,116,139,0.45)",fg: "#0f172a" },
        };
        const p = COLOR_MAP[landing.announcement_color] || COLOR_MAP.amber;
        return (
          <a
            href={landing.announcement_href || "#"}
            target={landing.announcement_href ? "_blank" : undefined}
            rel="noreferrer"
            data-testid="landing-announcement-bar"
            className="block mx-4 md:mx-6 mb-2 rounded-2xl px-4 py-2.5 text-center text-sm font-semibold shadow-sm border transition-transform hover:-translate-y-0.5"
            style={{ backgroundColor: p.bg, borderColor: p.border, color: p.fg }}
          >
            {landing.announcement_text}
          </a>
        );
      })()}

      <CoinsHeroBanner
        onCta={() => setTab("register")}
        referralActive={!!form.referred_by}
        content={landing}
      />
      <TestimonialsCarousel content={landing} />

      <main className="relative z-10 max-w-6xl mx-auto px-6 py-8">
        <div className="space-y-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-medium tracking-widest uppercase text-primary" data-testid="landing-section-intro">{landing.section_intro_label || "Novo · 2026"}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="landing-main-heading">
            {landing.main_heading || t("heroTitle")}
          </h1>
          <p className="text-lg text-muted-foreground max-w-md" data-testid="landing-main-subheading">{landing.main_subheading || t("heroSubtitle")}</p>

          {/* Live counters */}
          <div className="grid grid-cols-3 gap-2 max-w-lg" data-testid="landing-live-stats">
            <div className="p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400 font-semibold">Ao vivo</span>
              </div>
              <div className="text-2xl font-bold tracking-tight" data-testid="landing-total-users">
                {stats.total_users.toLocaleString("pt-BR")}
              </div>
              <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Users2 className="w-3 h-3" /> usuários
              </div>
            </div>
            <div className="p-3 rounded-xl border border-border bg-card">
              <div className="text-2xl font-bold tracking-tight" data-testid="landing-total-drivers">
                {stats.total_drivers.toLocaleString("pt-BR")}
              </div>
              <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Car className="w-3 h-3" /> motoristas
              </div>
            </div>
            <div className="p-3 rounded-xl border border-border bg-card">
              <div className="text-2xl font-bold tracking-tight" data-testid="landing-total-rides">
                {stats.total_rides_completed.toLocaleString("pt-BR")}
              </div>
              <div className="text-[11px] text-muted-foreground">corridas feitas</div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 max-w-lg">
            {[
              { icon: Users, label: t("social"), color: "text-blue-600" },
              { icon: Briefcase, label: t("work"), color: "text-orange-500" },
              { icon: Car, label: t("mobility"), color: "text-green-600" },
            ].map((f) => (
              <div key={f.label} className="p-4 rounded-xl border border-border bg-card hover-lift">
                <f.icon className={`w-6 h-6 ${f.color} mb-2`} />
                <div className="text-sm font-semibold">{f.label}</div>
              </div>
            ))}
          </div>

          {/* Sponsored ad slot */}
          <SponsoredAdWidget variant="inline" limit={1} className="max-w-lg" />
        </div>
      </main>

      {/* Community showcase: weekly ranking + live activity */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pb-16 grid md:grid-cols-2 gap-5">
        <WeeklyRanking />
        <LiveActivityFeed limit={12} />
      </section>
    </div>
  );
}
